import React from "react"
import SecondaryButton from "../Ui/Buttons/SecondaryButton"
import {
  Divider,
  FilterGroup,
  HeaderRow,
  Label,
  NumberOfHits,
  Show,
  TopProductSelectWrapper,
  Wrapper
} from "./TopProductsFilters.styled"
import PrimaryButton from "../Ui/Buttons/PrimaryButton"
import {
  filterByAll,
  filterByDiscount,
  filterByNonDiscount,
  filterByParent,
  filterByVariant,
  NUMBER_OF_HITS_OPTIONS
} from "./AnalyticsData"
import { useDispatch, useSelector } from "react-redux"
import {
  setTopProductsFilteredBy,
  setTopProductsFilteredByHits,
  setTopProductsFilteredByType,
  setTopProductsSortBy
} from "lib/store/services/analytics/slice"
import {
  selectProductNames,
  selectStoreMarkets,
  selectTopProductsExportData,
  selectTopProductsFilteredBy,
  selectTopProductsFilteredByHits,
  selectTopProductsFilteredByType,
  selectTopProductsSortBy
} from "lib/store/services/analytics/selectors"
import styled from "styled-components"
import { downloadCSV } from "helpers/csvExport"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { bucket, value } from "../../views/Dashboards/DashboardSalesAggregations"
import { Tooltip } from "../Ui/Tooltip"

const ExportButton = styled(SecondaryButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TopProductsFilters = () => {
  const dispatch = useDispatch()
  const filteredBy = useSelector(selectTopProductsFilteredBy)
  const filteredByType = useSelector(selectTopProductsFilteredByType)
  const sortBy = useSelector(selectTopProductsSortBy)
  const topProductsFilteredByHits = useSelector(selectTopProductsFilteredByHits)
  const exportData = useSelector(selectTopProductsExportData)
  const productNames = useSelector(selectProductNames)
  const selectedStoreMarkets = useSelector(selectStoreMarkets)

  const handleClickFilterBy = (value: string) => {
    dispatch(setTopProductsFilteredBy(value))
  }

  const handleClickFilterByType = (value: string) => {
    dispatch(setTopProductsFilteredByType(value))
  }

  const handleClickFilterByHits = (value: string) => {
    dispatch(setTopProductsFilteredByHits(Number.parseInt(value)))
  }

  const handleClickSortBy = (value: "quantity" | "totalSales") => {
    dispatch(setTopProductsSortBy(value))
  }

  const isSortByTotalSalesDisabled = selectedStoreMarkets.length !== 1

  function getProductParentName(parentId: string | undefined) {
    if (!parentId || !productNames) {
      return parentId
    }
    return productNames[parentId] ?? parentId
  }

  function toAverageDiscountPercentage(product: bucket): string {
    // Average discount in percentage can be calculated with a fixed currency
    const priceTotal = product.priceTotal?.value || 0
    const discountTotal = product.discountTotal?.value || 0

    if (discountTotal === 0) {
      return "0%"
    }

    const totalBeforeDiscount = priceTotal + discountTotal
    const discountPercentage = Math.round((discountTotal * 100) / totalBeforeDiscount)

    return `~${discountPercentage}%`
  }

  const handleExportCSV = () => {
    if (!exportData) {
      dispatch(
        alertActions.actions.createAlert({
          message: "No data available for export",
          type: "error"
        })
      )
      return
    }

    const { topProducts, productVariantStocks, currencyCode, ratesMap } = exportData

    const headers = [
      "Rank",
      "Product Name",
      "Parent ID",
      ...(filteredByType === filterByVariant ? ["Variant ID"] : []),
      "Total Quantity",
      `Total Sales (${currencyCode})`,
      "Top Market",
      "Top Store",
      ...(filteredBy === filterByAll || filteredBy === filterByDiscount
        ? ["Average Discount"]
        : []),
      ...(filteredByType === filterByVariant ? ["Stock"] : [])
    ]

    const rows = topProducts.map((product: bucket, index: number) => {
      const productName =
        filteredByType === filterByParent
          ? getProductParentName(product.topProductDetails?.hits.hits[0]._source.productParentId)
          : product.topProductDetails?.hits.hits[0]._source.name

      return [
        `#${index + 1}`,
        productName || "",
        product.topProductDetails?.hits.hits[0]._source.productParentId || "",
        ...(filteredByType === filterByVariant
          ? [product.topProductDetails?.hits.hits[0]._source.productVariantId || ""]
          : []),
        product.totalQuantity.value.toLocaleString(),
        // Calculate total sales in the selected currency
        (() => {
          let totalSales = 0
          product.totalValuesPerCurrencyCode?.buckets?.forEach(
            (bucket: {
              doc_count: number
              key: string
              totalPriceAmount: value
              totalTaxAmount: value
            }) => {
              const rate = ratesMap[bucket.key]?.[currencyCode] || 1
              totalSales += (bucket.totalPriceAmount?.value || 0) * rate
            }
          )
          return Math.round(totalSales / 100).toLocaleString()
        })(),
        product.ordersTopValues?.topCountries.buckets[0]?.key || "",
        product.ordersTopValues?.topStores.buckets[0]?.key || "",
        ...(filteredBy === filterByAll || filteredBy === filterByDiscount
          ? [toAverageDiscountPercentage(product)]
          : []),
        ...(filteredByType === filterByVariant
          ? [
              product.topProductDetails?.hits.hits[0]._source.productVariantId
                ? productVariantStocks[
                    product.topProductDetails?.hits.hits[0]._source.productVariantId
                  ]?.toLocaleString() || "N/A"
                : "N/A"
            ]
          : [])
      ]
    })

    const csvData = [headers, ...rows]
    const timestamp = new Date().toISOString().split("T")[0]
    const filterDesc = `${filteredByType}-${filteredBy}`
    const filename = `top-products-${filterDesc}-${timestamp}.csv`

    downloadCSV(csvData, filename)

    // Dispatch success notification
    dispatch(
      alertActions.actions.createAlert({
        message: `Top products data exported successfully to ${filename}`,
        type: "success"
      })
    )
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Label>Top Products</Label>
      </HeaderRow>
      <TopProductSelectWrapper>
        <FilterGroup>
          {filteredBy === filterByAll ? (
            <PrimaryButton>All</PrimaryButton>
          ) : (
            <SecondaryButton handleClick={() => handleClickFilterBy(filterByAll)}>
              All
            </SecondaryButton>
          )}

          {filteredBy === filterByDiscount ? (
            <PrimaryButton>Discounted</PrimaryButton>
          ) : (
            <SecondaryButton handleClick={() => handleClickFilterBy(filterByDiscount)}>
              Discounted
            </SecondaryButton>
          )}

          {filteredBy === filterByNonDiscount ? (
            <PrimaryButton>Non Discounted</PrimaryButton>
          ) : (
            <SecondaryButton handleClick={() => handleClickFilterBy(filterByNonDiscount)}>
              Non Discounted
            </SecondaryButton>
          )}
        </FilterGroup>
        <Divider />
        <FilterGroup>
          {filteredByType.includes(filterByVariant) ? (
            <>
              <SecondaryButton handleClick={() => handleClickFilterByType(filterByParent)}>
                By Product Parent
              </SecondaryButton>
              <PrimaryButton handleClick={() => handleClickFilterByType(filterByVariant)}>
                By Product Variant
              </PrimaryButton>
            </>
          ) : (
            <>
              <PrimaryButton handleClick={() => handleClickFilterByType(filterByParent)}>
                By Product Parent
              </PrimaryButton>
              <SecondaryButton handleClick={() => handleClickFilterByType(filterByVariant)}>
                By Product Variant
              </SecondaryButton>
            </>
          )}
        </FilterGroup>
        <Divider />
        <FilterGroup>
          <Tooltip id="sort-by-tooltip" />
          {sortBy === "quantity" ? (
            <PrimaryButton>Sort by Quantity</PrimaryButton>
          ) : (
            <SecondaryButton handleClick={() => handleClickSortBy("quantity")}>
              Sort by Quantity
            </SecondaryButton>
          )}
          {sortBy === "totalSales" ? (
            <PrimaryButton
              handleClick={
                isSortByTotalSalesDisabled ? undefined : () => handleClickSortBy("totalSales")
              }
              data-tooltip-id="sort-by-tooltip"
              data-tooltip-content={
                isSortByTotalSalesDisabled
                  ? "Please select exactly one market to sort by total sales"
                  : ""
              }
            >
              Sort by Sales
            </PrimaryButton>
          ) : (
            <SecondaryButton
              handleClick={
                isSortByTotalSalesDisabled ? undefined : () => handleClickSortBy("totalSales")
              }
              data-tooltip-id="sort-by-tooltip"
              data-tooltip-content={
                isSortByTotalSalesDisabled
                  ? "Please select exactly one market to sort by total sales"
                  : ""
              }
            >
              Sort by Total Sales
            </SecondaryButton>
          )}
        </FilterGroup>
        <Divider />
        {NUMBER_OF_HITS_OPTIONS && topProductsFilteredByHits && (
          <Show>
            {NUMBER_OF_HITS_OPTIONS.map((hits) => (
              <NumberOfHits
                key={hits}
                onClick={() => handleClickFilterByHits(hits)}
                $active={hits === topProductsFilteredByHits.toString()}
              >
                {hits}
              </NumberOfHits>
            ))}
          </Show>
        )}
        <Divider />
        <ExportButton
          handleClick={handleExportCSV}
          data-tooltip-id="export-csv-tooltip"
          data-tooltip-content="Export to CSV"
        >
          Export
        </ExportButton>
        <Tooltip id="export-csv-tooltip" />
      </TopProductSelectWrapper>
    </Wrapper>
  )
}

export default TopProductsFilters
